import { Controller } from '@hotwired/stimulus'

// Site menu panel (shared/_cluster_nav). Opens from the header buttons and the
// mobile Menu button via the `site-nav:open` event (marketing_nav_controller).
//
// Besides open/close, tabs, ESC and the focus trap it:
//   * loads thumbnails only when the menu opens (img[data-nav-src]), picking
//     the -dark file while html.app-dark is set and swapping on theme change;
//   * builds the Research state grid + model chips on first view (kept out of
//     the server HTML so every page doesn't link to 50 /market pages);
//   * runs the Shoppers VIN box (History / Value / Sticker);
//   * sends GA4 events nav_open, nav_tab, nav_click, nav_hero_submit.
//     None of them is a key event. They go through window.vaTrack (one push
//     into dataLayer + direct gtag hit) because no GTM tag owns these names —
//     see docs/analytics_gtm_checklist.md and shared/_outbound_clicks.
const STORAGE_KEY = 'vaNavSection'

export default class extends Controller {
  static values = { css: String }
  static targets = ['panel', 'scrim', 'tab', 'section', 'tabs', 'vinForm', 'vinInput', 'vinStatus', 'modelInput', 'chips', 'stateMap']

  connect() {
    this.boundOpen = event => this.open(event.detail)
    this.boundKey = event => this.onKey(event)
    this.boundSearch = () => { if (!this.panelTarget.hidden) this.close() }
    this.boundClick = event => this.trackClick(event)
    document.addEventListener('site-nav:open', this.boundOpen)
    document.addEventListener('keydown', this.boundKey, true)
    document.addEventListener('seo-search:open', this.boundSearch)
    this.panelTarget.addEventListener('click', this.boundClick, true)
    this.mode = 'history'
    // Pages that inline their CSS (VDP, showroom) don't link the panel sheet:
    // fetch it when the visitor reaches for the menu, before the click lands.
    this.boundPrefetch = event => {
      if (event.target.closest?.('[aria-controls="seo-cnav-panel"]')) this.loadCss()
    }
    if (!this.cssReady()) {
      ['pointerover', 'focusin', 'touchstart'].forEach(type =>
        document.addEventListener(type, this.boundPrefetch, { passive: true }))
    }
  }

  disconnect() {
    document.removeEventListener('site-nav:open', this.boundOpen)
    document.removeEventListener('keydown', this.boundKey, true)
    document.removeEventListener('seo-search:open', this.boundSearch)
    this.panelTarget.removeEventListener('click', this.boundClick, true)
    this.themeObserver?.disconnect()
    this.removePrefetch()
    this.releaseBackground()
  }

  // ------------------------------------------------------ lazy panel CSS

  cssReady() {
    return getComputedStyle(this.panelTarget).getPropertyValue('--nav-ready').trim() === '1'
  }

  removePrefetch() {
    ['pointerover', 'focusin', 'touchstart'].forEach(type =>
      document.removeEventListener(type, this.boundPrefetch, { passive: true }))
  }

  // Until the sheet arrives the inline shell shows a skeleton (seo-search.scss).
  loadCss() {
    if (this.cssRequested || this.cssReady() || !this.cssValue) return
    this.cssRequested = true
    this.removePrefetch()
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = this.cssValue
    link.dataset.siteNavCss = ''
    // A failed sheet must never leave a phone on a blank, scroll-locked
    // screen: close the panel (restores overflow + inert) and retry next time.
    link.onerror = () => {
      this.cssRequested = false
      link.remove()
      if (!this.panelTarget.hidden) this.close()
    }
    // Hidden (skeleton) elements can't take focus — move it in once styled.
    link.onload = () => {
      if (!this.panelTarget.hidden && !this.panelTarget.contains(document.activeElement)) {
        this.tabTargets.find(tab => tab.dataset.section === this.selected)?.focus()
      }
    }
    document.head.appendChild(link)
  }

  // ------------------------------------------------------------ open / close

  open({ section, trigger, remember } = {}) {
    let wanted = section
    if (remember) {
      try { wanted = localStorage.getItem(STORAGE_KEY) || section } catch (e) { wanted = section }
    }
    if (!this.panelTarget.hidden) {
      this.select(wanted)
      return
    }
    this.trigger = trigger || document.activeElement
    this.loadCss()
    this.select(wanted, { silent: true })
    this.panelTarget.hidden = false
    this.scrimTarget.hidden = false
    this.trigger?.setAttribute('aria-expanded', 'true')
    this.savedOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    // Include body-level widgets as well as the page's header and content.
    this.background = []
    let branch = this.element
    while (branch.parentElement) {
      this.background.push(...[...branch.parentElement.children]
        .filter(element => element !== branch)
        .map(element => ({ element, inert: element.inert })))
      if (branch.parentElement === document.body) break
      branch = branch.parentElement
    }
    this.background.forEach(({ element }) => { element.inert = true })
    this.loadImages()
    this.watchTheme()
    if (this.cssReady()) {
      this.tabTargets.find(tab => tab.dataset.section === this.selected)?.focus()
    } else {
      // Skeleton: only × is visible. Give up after 6s rather than strand anyone.
      this.panelTarget.querySelector('.site-nav__close')?.focus()
      clearTimeout(this.cssTimer)
      this.cssTimer = setTimeout(() => {
        if (!this.cssReady() && !this.panelTarget.hidden) this.close()
      }, 6000)
    }
    this.track('nav_open', { nav_section: this.selected, nav_trigger: trigger?.dataset?.navTrigger || (remember ? 'menu' : 'header') })
  }

  close() {
    clearTimeout(this.cssTimer)
    this.panelTarget.hidden = true
    this.scrimTarget.hidden = true
    this.trigger?.setAttribute('aria-expanded', 'false')
    this.releaseBackground()
    const visibleTrigger = this.trigger?.isConnected && this.trigger.getClientRects().length
      ? this.trigger
      : [...document.querySelectorAll('[aria-controls="seo-cnav-panel"]')].find(element => element.getClientRects().length)
    visibleTrigger?.focus()
  }

  releaseBackground() {
    if (this.savedOverflow !== undefined) document.body.style.overflow = this.savedOverflow
    this.savedOverflow = undefined
    this.background?.forEach(({ element, inert }) => { element.inert = inert })
    this.background = null
  }

  followLink(event) {
    if (event.target.closest('a[href]')) this.close()
  }

  openSearch() {
    this.close()
    document.dispatchEvent(new CustomEvent('seo-search:open'))
  }

  // ------------------------------------------------------------------ tabs

  select(section, { silent = false } = {}) {
    const previous = this.selected
    this.selected = this.tabTargets.some(tab => tab.dataset.section === section) ? section : 'shoppers'
    this.tabTargets.forEach(tab => {
      const selected = tab.dataset.section === this.selected
      tab.setAttribute('aria-selected', String(selected))
      tab.tabIndex = selected ? 0 : -1
    })
    this.sectionTargets.forEach(panel => { panel.hidden = panel.dataset.section !== this.selected })
    // A new tab starts at its top (otherwise you land mid-section, past the banner).
    const content = this.panelTarget.querySelector('.site-nav__content')
    if (content && previous !== this.selected) content.scrollTop = 0
    if (!this.panelTarget.hidden) this.loadImages()
    this.panelTarget.dataset.section = this.selected
    if (this.selected === 'research') this.buildResearch()
    try { localStorage.setItem(STORAGE_KEY, this.selected) } catch (e) { /* private mode */ }
    if (!silent && previous && previous !== this.selected && !this.panelTarget.hidden) {
      this.track('nav_tab', { nav_section: this.selected, nav_from: previous })
    }
  }

  selectSection(event) { this.select(event.currentTarget.dataset.section) }

  onTabKey(event) {
    const index = this.tabTargets.indexOf(event.currentTarget)
    let next
    if (['ArrowDown', 'ArrowRight'].includes(event.key)) next = (index + 1) % this.tabTargets.length
    if (['ArrowUp', 'ArrowLeft'].includes(event.key)) next = (index - 1 + this.tabTargets.length) % this.tabTargets.length
    if (event.key === 'Home') next = 0
    if (event.key === 'End') next = this.tabTargets.length - 1
    if (next === undefined) return
    event.preventDefault()
    this.select(this.tabTargets[next].dataset.section)
    this.tabTargets[next].focus()
  }

  onKey(event) {
    if (this.panelTarget.hidden) return
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault()
      event.stopImmediatePropagation()
      this.openSearch()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      this.close()
    } else if (event.key === 'Tab') {
      const focusable = [...this.panelTarget.querySelectorAll('a[href], button, input, [tabindex="0"]')]
        .filter(element => element.tabIndex >= 0 && !element.disabled && element.getClientRects().length)
      const first = focusable[0], last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last?.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first?.focus()
      }
    }
  }

  // ------------------------------------------------- thumbnails + theme

  isDark() { return document.documentElement.classList.contains('app-dark') }

  // Only the sections someone looks at (or points at) download thumbnails:
  // the active tab on open, others on tab hover/focus (~90 KB in total).
  loadImages(section = this.selected) {
    if (!this.loadedSections) this.loadedSections = new Set()
    this.loadedSections.add(section)
    this.paintImages(this.sectionTargets.find(panel => panel.dataset.section === section))
  }

  paintImages(root) {
    if (!root) return
    const dark = this.isDark()
    root.querySelectorAll('img[data-nav-src]').forEach(img => {
      const src = (dark && img.dataset.navSrcDark) || img.dataset.navSrc
      if (img.getAttribute('src') !== src) img.setAttribute('src', src)
    })
  }

  prefetchTab(event) {
    const section = event.currentTarget.dataset.section
    if (!this.panelTarget.hidden && !this.loadedSections?.has(section)) this.loadImages(section)
  }

  watchTheme() {
    if (this.themeObserver) return
    this.themeObserver = new MutationObserver(() => {
      if (!this.panelTarget.hidden) this.loadedSections?.forEach(section => this.loadImages(section))
    })
    this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
  }

  // ------------------------------------------------------ Shoppers VIN box

  setMode(event) {
    const mode = event.currentTarget.dataset.mode
    this.mode = mode
    const form = this.vinFormTarget
    form.dataset.mode = mode
    form.querySelectorAll('[data-mode]').forEach(button => {
      if (button.classList.contains('site-nav__seg-btn')) button.setAttribute('aria-pressed', String(button.dataset.mode === mode))
    })
    form.querySelectorAll('[data-mode-only]').forEach(el => { el.hidden = el.dataset.modeOnly !== mode })
    form.querySelectorAll('[data-mode-hide]').forEach(el => { el.hidden = el.dataset.modeHide === mode })
    // The sticker block sits on the VehiLeaks vehicle page; GET keeps the hash
    // through VehiLeaks' /?vin= redirect.
    form.action = mode === 'sticker' ? 'https://vehileaks.com/#window-sticker' : 'https://vehileaks.com/'
    this.vinStatusTarget.textContent = ''
    if (mode !== 'value') this.vinInputTarget.focus()
  }

  // I, O and Q never appear in a VIN; drop them like the homepage form does.
  syncVin() {
    const input = this.vinInputTarget
    const vin = input.value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '').slice(0, 17)
    if (vin !== input.value) input.value = vin
    this.vinStatusTarget.textContent = ''
  }

  submitVin(event) {
    this.syncVin()
    const valid = this.vinInputTarget.value.length === 17
    this.track('nav_hero_submit', { nav_section: 'shoppers', nav_mode: this.mode, vin_valid: valid })
    if (valid) return
    event.preventDefault()
    this.vinInputTarget.focus()
    this.vinStatusTarget.textContent = `A VIN is 17 characters — ${17 - this.vinInputTarget.value.length} to go.`
  }

  // -------------------------------------------------------- Research tab

  // Typing a model hands off to the site search palette, which already knows
  // every make/model page. Without JS the form simply opens /prices.
  submitModel(event) {
    event.preventDefault()
    const query = this.modelInputTarget.value.trim()
    this.track('nav_hero_submit', { nav_section: 'research', nav_mode: 'model_search', has_query: query.length > 0 })
    this.close()
    document.dispatchEvent(new CustomEvent('seo-search:open', { detail: { query } }))
  }

  buildResearch() {
    if (this.researchBuilt || !this.hasStateMapTarget) return
    this.researchBuilt = true
    let data
    try { data = JSON.parse(this.sectionTargets.find(s => s.dataset.section === 'research').dataset.research) } catch (e) { return }

    const chips = document.createDocumentFragment()
    data.models.forEach(([label, href]) => chips.appendChild(this.link(href, label, 'site-nav__chip', 'model-chip')))
    chips.appendChild(this.link('/prices', 'All makes →', 'site-nav__chip', 'all-makes'))
    this.chipsTarget.appendChild(chips)

    const map = document.createDocumentFragment()
    data.states.forEach(([code, name, href, row, col]) => {
      const a = this.link(href, code, 'site-nav__state', 'state')
      a.style.gridRow = row + 1
      a.style.gridColumn = col + 1
      a.setAttribute('aria-label', `${name} car prices`)
      a.title = name
      map.appendChild(a)
    })
    this.stateMapTarget.appendChild(map)
    this.stateMapTarget.classList.add('is-ready')
  }

  link(href, text, className, item) {
    const a = document.createElement('a')
    a.href = href
    a.className = className
    a.textContent = text
    a.dataset.turbo = 'false'
    a.dataset.navItem = item
    return a
  }

  // --------------------------------------------------------------- GA4

  trackClick(event) {
    const link = event.target.closest('a[href]')
    if (!link || !this.panelTarget.contains(link)) return
    const section = link.closest('[data-cluster-nav-target="section"]')?.dataset.section || this.selected
    const external = link.hostname && link.hostname !== window.location.hostname
    this.track('nav_click', {
      nav_section: section,
      nav_item: link.dataset.navItem || link.textContent.trim().slice(0, 40),
      nav_kind: link.dataset.navKind || (link.closest('.site-nav__footer') ? 'footer' : 'link'),
      nav_external: Boolean(external),
      link_url: link.href.split('?')[0]
    })
  }

  track(name, params) {
    try {
      if (typeof window.vaTrack === 'function') {
        window.vaTrack(name, params)
      } else {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({ event: name, ...params })
      }
    } catch (e) { /* analytics must never break the menu */ }
  }
}
